import React from 'react';
import { motion } from 'framer-motion';
import { Footer } from '../components/Footer';
import { Maximize, ArrowUpToLine, Users, CheckCircle2 } from 'lucide-react';
export function FacilitiesPage() {
  const mainVenues = [{
    title: 'The Azure Hall',
    capacity: 'Up to 300 Guests',
    sqft: '8,000 sq ft',
    ceiling: '20ft vaulted ceilings',
    description: 'Our flagship ballroom featuring soaring ceilings, crystal chandeliers, and a dedicated bridal suite. The perfect canvas for your dream wedding or grand gala.',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80',
    amenities: ['Built-in Dance Floor', 'Custom LED Lighting System', 'Private Bridal Suite', 'Direct Kitchen Access', 'Floor-to-ceiling Windows']
  }, {
    title: 'The Crystal Lounge',
    capacity: 'Up to 150 Guests',
    sqft: '4,500 sq ft',
    ceiling: '14ft ceilings',
    description: 'An intimate yet vibrant space with built-in DJ booth, dynamic lighting, and a full-service bar. Celebrate in style for birthdays, anniversaries, and social mixers.',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&q=80',
    amenities: ['Built-in DJ Booth', 'Full-Service Marble Bar', 'Lounge Seating Areas', 'Integrated Sound System', 'Coat Check Room']
  }, {
    title: 'The Sapphire Room',
    capacity: 'Up to 200 Guests',
    sqft: '6,000 sq ft',
    ceiling: '16ft ceilings',
    description: 'Sophisticated and versatile, equipped with state-of-the-art AV technology and a private entrance. Ideal for galas, fundraisers, and executive retreats.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
    amenities: ['State-of-the-art AV', 'Private Entrance', 'Acoustic Paneling', 'Modular Staging', 'Dedicated Restrooms']
  }];
  const additionalFacilities = [{
    name: 'Bridal Suite',
    desc: 'Luxurious private suite with vanity stations, lounge area, and en-suite bathroom.'
  }, {
    name: 'Executive Boardroom',
    desc: 'Intimate meeting space for up to 12 guests with video conferencing capabilities.'
  }, {
    name: 'Outdoor Terrace',
    desc: '2,000 sq ft patio perfect for cocktail hours or open-air ceremonies.'
  }, {
    name: 'Commercial Kitchen',
    desc: 'Fully equipped 3,000 sq ft prep and cooking facility for our culinary team.'
  }];
  return <motion.main initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full bg-h2o-950 text-slate-100 flex flex-col pt-20" data-id="element-327">
      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden" data-id="element-328">
        <div className="absolute inset-0 z-0" data-id="element-329">
          <img src="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1920&q=80" alt="H2O Facilities" className="w-full h-full object-cover object-center" data-id="element-330" />
          <div className="absolute inset-0 bg-h2o-950/80" data-id="element-331" />
        </div>
        <div className="relative z-10 text-center px-6" data-id="element-332">
          <motion.h1 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-4xl md:text-6xl font-bold text-white mb-4" data-id="element-333">
            Our Facilities
          </motion.h1>
          <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-334">
            Explore our meticulously designed spaces for events of any scale.
          </motion.p>
        </div>
      </section>

      {/* Main Venues */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto w-full space-y-24" data-id="element-335">
        {mainVenues.map((venue, index) => <motion.div key={venue.title} initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center`} data-id="element-336">
            <div className="w-full lg:w-1/2" data-id="element-337">
              <div className="rounded-lg overflow-hidden border border-slate-700/50 shadow-2xl relative group" data-id="element-338">
                <div className="absolute inset-0 bg-h2o-cyan/10 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none" data-id="element-339" />
                <img src={venue.image} alt={venue.title} className="w-full h-auto object-cover aspect-[4/3] transform group-hover:scale-105 transition-transform duration-700" data-id="element-340" />
              </div>
            </div>
            <div className="w-full lg:w-1/2 space-y-6" data-id="element-341">
              <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-342">
                {venue.title}
              </h2>
              <div className="flex flex-wrap gap-4 text-sm font-medium text-slate-300" data-id="element-343">
                <span className="flex items-center bg-h2o-900 px-3 py-1.5 rounded-md border border-slate-700" data-id="element-344">
                  <Users className="w-4 h-4 mr-2 text-h2o-cyan" data-id="element-345" />{' '}
                  {venue.capacity}
                </span>
                <span className="flex items-center bg-h2o-900 px-3 py-1.5 rounded-md border border-slate-700" data-id="element-346">
                  <Maximize className="w-4 h-4 mr-2 text-h2o-cyan" data-id="element-347" />{' '}
                  {venue.sqft}
                </span>
                <span className="flex items-center bg-h2o-900 px-3 py-1.5 rounded-md border border-slate-700" data-id="element-348">
                  <ArrowUpToLine className="w-4 h-4 mr-2 text-h2o-cyan" data-id="element-349" />{' '}
                  {venue.ceiling}
                </span>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed" data-id="element-350">
                {venue.description}
              </p>
              <div data-id="element-351">
                <h4 className="text-white font-semibold mb-3" data-id="element-352">Key Amenities</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-353">
                  {venue.amenities.map(amenity => <li key={amenity} className="flex items-start text-slate-400" data-id="element-354">
                      <CheckCircle2 className="w-5 h-5 mr-2 text-h2o-cyan shrink-0" data-id="element-355" />
                      <span data-id="element-356">{amenity}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </motion.div>)}
      </section>

      {/* Additional Facilities */}
      <section className="py-24 px-6 md:px-12 bg-h2o-900 border-t border-slate-800" data-id="element-357">
        <div className="max-w-7xl mx-auto" data-id="element-358">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-359">
            Additional Facilities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-360">
            {additionalFacilities.map((fac, i) => <motion.div key={fac.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-h2o-950 border border-slate-700/50 rounded-lg p-6 hover:border-h2o-cyan/30 transition-colors" data-id="element-361">
                <div className="w-full h-32 bg-h2o-800 rounded-md mb-4 flex items-center justify-center text-slate-600" data-id="element-362">
                  <span className="text-sm uppercase tracking-widest font-medium" data-id="element-363">
                    Photo
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-364">
                  {fac.name}
                </h3>
                <p className="text-sm text-slate-400" data-id="element-365">{fac.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-366" />
    </motion.main>;
}